/**
 * Dashboard V2 - Aggregate Analytics Dashboard
 * Displays summary metrics across multiple clients
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import { useAuth } from "../lib/AuthContext";
import XanoAPI from "../lib/XanoAPI";
import { DashboardFilterProvider } from "../components/dashboard-v2/contexts/DashboardFilterContext";
import { AgentSelector } from "../components/dashboard-v2/components/AgentSelector";
import { DashboardFilters } from "../components/dashboard-v2/components/DashboardFilters";
import { useDashboardFilters } from "../components/dashboard-v2/contexts/DashboardFilterContext";
import { useFilteredClients } from "../components/dashboard-v2/hooks/useFilteredClients";
import { useDashboardLayout } from "../components/dashboard-v2/hooks/useDashboardLayout";
import { ClientType } from "../types";
import { useChartData } from "../components/dashboard-v2/utils/useChartData";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCw,
  Users,
  UserCheck,
  UserCircle,
  MapPin,
  BookOpen,
  Building2,
  Save,
  Check,
  X
} from "lucide-react";
import { DashboardGrid } from "../components/dashboard-v2/components/DashboardGrid";
import { DashboardControls } from "../components/dashboard-v2/components/DashboardControls";
import { WidgetLibrary } from "../components/dashboard-v2/components/WidgetLibrary";
import { LayoutSwitcher } from "../components/dashboard-v2/components/LayoutSwitcher";
import GoogleMapsProvider from "../components/dashboard/GoogleMapProvider";
import GoogleHeatmap from "../components/dashboard/GoogleHeatmap";
import AboutCompany from "../components/dashboard/AboutCompany";
import config from "../config";
import { 
  removeWidgetFromLayout, 
  duplicateWidgetInLayout,
  toggleWidgetVisibility,
} from "../components/dashboard-v2/utils/layoutHelpers";
import { getWidgetTitle } from "../components/dashboard-v2/utils/widgetRegistry";
import { WIDGET_COMPONENTS } from "../components/dashboard-v2/utils/widgetComponentsWrapped";
import { DashboardWidget } from "../components/dashboard-v2/types";
import { DashboardLayout } from "../components/dashboard-v2/types";
import {
  loadLayoutFromAPI,
  saveLayoutToStorage,
  getDefaultLayout,
} from "../components/dashboard-v2/utils/storageHelpers";

const queryClient = new QueryClient();

/**
 * Main dashboard content (needs to be inside filter provider)
 */
interface DashboardV2ContentProps {
  isFluidLayout: boolean;
  onLayoutModeChange: (isFluid: boolean) => void;
}

const DashboardV2Content: React.FC<DashboardV2ContentProps> = ({ 
  isFluidLayout: propIsFluidLayout,
  onLayoutModeChange 
}) => {
  const api = XanoAPI.getInstance();
  const auth = useAuth();
  const { filters } = useDashboardFilters();
  const isAdmin = auth?.currentUser?.user_permissions?.[0]?.role === "admin";

  // Fetch clients based on selected agents
  const { data: clientsData, isLoading, error, refetch: refetchClients } = useQuery({
    queryKey: [
      "dashboard-clients",
      filters.selectedAgentIds,
      auth?.currentUser?.id,
    ],
    queryFn: async () => {
      // For admin with selected agents, fetch for each agent
      if (isAdmin && filters.selectedAgentIds.length > 0) {
        const allClients: ClientType[] = [];
        for (const agentId of filters.selectedAgentIds) {
          const response = await api.getAllClients(
            1,
            "",
            5000, // Reduced page size to avoid 400 errors
            {},
            String(agentId)
          );
          if (response.items && Array.isArray(response.items)) {
            allClients.push(...response.items);
          }
        }
        return { items: allClients };
      } else {
        // Agent's own clients (or admin with no selection)
        return await api.getAllClients(1, "", 5000, {});
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!auth?.currentUser,
  });

  // Use sample data if no real data available (for testing/demo)
  const useSampleData = !clientsData?.items || clientsData.items.length === 0;
  
  // Load chart data from JSON for testing (set to false when API endpoints are ready)
  const USE_JSON_CHART_DATA = true; // Set to false to use calculated data from clients
  const chartDataFromJSON = useChartData(USE_JSON_CHART_DATA);
  
  const clients = clientsData?.items || [];
  
  // Log all clients to console
  useEffect(() => {
    if (clients && clients.length > 0) {
      console.log("📋 All Clients (filtered by selected agents):", clients);
      console.log("📋 Total clients count:", clients.length);
    }
  }, [clients]);
  
  const filteredClients = useFilteredClients(clients);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Determine userId to pass to widgets
  // If a single agent is selected, use that agent's user_id; otherwise undefined (all agents)
  const userIdForWidgets = useMemo(() => {
    if (isAdmin && filters.selectedAgentIds.length === 1) {
      // Single agent selected - use their user_id
      return filters.selectedAgentIds[0];
    }
    // Multiple agents or no agents selected - pass undefined (endpoint returns all)
    return undefined;
  }, [isAdmin, filters.selectedAgentIds]);

  // Fetch ALL clients for the map (not filtered by selected agents)
  // This ensures the map shows complete client distribution across all states
  const { data: allClientsForMap, isLoading: isLoadingAllClients } = useQuery({
    queryKey: ["all-clients-for-map", auth?.currentUser?.id],
    queryFn: async () => {
      // Fetch ALL clients for the authenticated user's agency (no agent filtering)
      const response = await api.getAllClients(1, "", 5000, {});
      const items = response.items || [];
      
      console.log("🗺️ Map: Fetched all clients for map:", items.length);
      console.log("🗺️ Map: All clients (complete list):", items);
      return items;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!auth?.currentUser?.module_crm_plan,
  });

  // Fetch location insights for Google Map
  // This API endpoint should return state-level aggregated data
  const { data: locationInsightsData, isLoading: isLoadingLocationInsights } = useQuery({
    queryKey: ["location-insights-map", userIdForWidgets],
    queryFn: async () => {
      try {
        const result = await api.getLocationInsights(userIdForWidgets);
        return result;
      } catch (error) {
        console.error("🗺️ Map: Error fetching location insights:", error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!auth?.currentUser?.module_crm_plan,
    retry: 1,
  });

  // State abbreviation to full name mapping (usStatesShape uses full names like "Virginia", not "VA")
  const stateAbbrToFullName: Record<string, string> = {
    "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
    "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
    "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
    "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
    "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
    "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
    "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
    "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
    "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
    "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming",
    "DC": "District of Columbia"
  };

  // Prepare location insights data for Google Heatmap
  // GoogleHeatmap uses FULL STATE NAMES (e.g., "Virginia") from usStatesShape, not abbreviations (e.g., "VA")
  // We need to convert state abbreviations to full names to match
  const locationDataForMap = useMemo(() => {
    console.log("🗺️ Map: =========================================");
    console.log("🗺️ Map: Computing locationDataForMap");
    console.log("🗺️ Map: isLoadingLocationInsights:", isLoadingLocationInsights);
    console.log("🗺️ Map: locationInsightsData:", locationInsightsData);
    console.log("🗺️ Map: locationInsightsData?.locations?.length:", locationInsightsData?.locations?.length || 0);
    console.log("🗺️ Map: isLoadingAllClients:", isLoadingAllClients);
    console.log("🗺️ Map: allClientsForMap length:", allClientsForMap?.length || 0);
    
    // Use API data if available and has locations
    if (!isLoadingLocationInsights && locationInsightsData?.locations && locationInsightsData.locations.length > 0) {
      console.log("🗺️ Map: ✅ Using API location insights data");
      console.log("🗺️ Map: Total locations from API:", locationInsightsData.locations.length);
      
      // Log all location data to verify it's real data
      const totalClientsFromAPI = locationInsightsData.locations.reduce((sum: number, loc: any) => sum + (loc.count || 0), 0);
      console.log("🗺️ Map: Total clients from API:", totalClientsFromAPI);
      console.log("🗺️ Map: All API locations with counts:", locationInsightsData.locations.map((loc: any) => ({
        name: loc.name,
        state: loc.state,
        count: loc.count
      })));
      
      // Check if API data is complete - if it's missing too many clients, use client data fallback
      const totalAvailableClients = allClientsForMap?.length || 0;
      const apiCompletenessRatio = totalClientsFromAPI / totalAvailableClients;
      
      console.log("🗺️ Map: Total available clients:", totalAvailableClients);
      console.log("🗺️ Map: API completeness ratio:", apiCompletenessRatio);
      
      // If API is missing more than 20% of clients, fall back to calculating from client data
      if (totalAvailableClients > 0 && apiCompletenessRatio < 0.8) {
        console.warn(`🗺️ Map: ⚠️ API data incomplete (${totalClientsFromAPI}/${totalAvailableClients} clients). Falling back to client data calculation.`);
        // Fall through to client data calculation below
      } else {
        const mappedData = locationInsightsData.locations
          .filter((location: any) => {
            return location && (location.name || location.state || location.stateProvince || location.state_province);
          })
          .map((location: any) => {
            const stateValue = (location.name || location.state || location.stateProvince || location.state_province || "").trim();
            
            if (!stateValue) {
              return null;
            }
            
            const stateAbbr = stateValue.toUpperCase();
            
            // Convert abbreviation to full name if needed
            let fullName: string;
            if (stateValue.length === 2) {
              fullName = stateAbbrToFullName[stateAbbr] || stateValue;
            } else {
              fullName = stateValue;
            }
            
            return {
              state: fullName,
              weight: location.count || 0,
            };
          })
          .filter((item: { state: string; weight: number } | null): item is { state: string; weight: number } => item !== null);
        
        console.log("🗺️ Map: Mapped data (first 10):", mappedData.slice(0, 10));
        console.log("🗺️ Map: Total states in mapped data:", mappedData.length);
        const totalClientsInMapped = mappedData.reduce((sum, item) => sum + item.weight, 0);
        console.log("🗺️ Map: Total clients in mapped data:", totalClientsInMapped);
        
        return mappedData;
      }
    }
    
    // Fallback: calculate from ALL clients if API data is not available or incomplete
    const allClients = allClientsForMap || [];
    console.log("🗺️ Map: ⚠️ API data not available or incomplete, using client data fallback");
    console.log("🗺️ Map: Total clients for fallback:", allClients.length);
    
    if (allClients.length === 0) {
      console.log("🗺️ Map: ⚠️ No clients available yet");
      return [];
    }
    
    const stateCounts: Record<string, number> = {};
    let clientsWithState = 0;
    let clientsWithoutState = 0;
    
    allClients.forEach((client) => {
      const stateValue = client?.stateProvince;
      
      if (stateValue && typeof stateValue === 'string' && stateValue.trim()) {
        clientsWithState++;
        const stateAbbr = stateValue.toUpperCase().trim();
        const fullName = stateAbbrToFullName[stateAbbr];
        
        if (fullName) {
          stateCounts[fullName] = (stateCounts[fullName] || 0) + 1;
        } else if (stateValue.length > 2) {
          stateCounts[stateValue] = (stateCounts[stateValue] || 0) + 1;
        } else {
          console.warn(`🗺️ Map: Unknown state abbreviation "${stateAbbr}" for client ${client.id}`);
        }
      } else {
        clientsWithoutState++;
      }
    });

    console.log("🗺️ Map: Clients with stateProvince:", clientsWithState);
    console.log("🗺️ Map: Clients without stateProvince:", clientsWithoutState);
    console.log("🗺️ Map: States found:", Object.keys(stateCounts).length);
    console.log("🗺️ Map: State counts:", stateCounts);

    if (Object.keys(stateCounts).length === 0) {
      console.warn("🗺️ Map: ⚠️ No states found in client data!");
      return [];
    }

    const result = Object.entries(stateCounts).map(([state, count]) => ({
      state,
      weight: count,
    }));
    
    console.log("🗺️ Map: ✅ Returning", result.length, "states from client data");
    console.log("🗺️ Map: Total clients in result:", result.reduce((sum, item) => sum + item.weight, 0));
    console.log("🗺️ Map: =========================================");
    return result;
  }, [locationInsightsData, allClientsForMap, isLoadingLocationInsights, isLoadingAllClients]);
  
  // Dashboard layout management
  // Load layout from API on mount
  const hasLoadedFromAPIRef = useRef<boolean>(false); // Track if we've loaded from API to prevent overwriting
  
  const [currentLayout, setCurrentLayout] = useState<DashboardLayout>(() => {
    // Start with default layout - API will load and update it
    const layout = getDefaultLayout();
    console.log("DashboardV2: Initial state - using default layout");
    return layout;
  });
  
  const [saveStatus, setSaveStatus] = useState<{ success: boolean; error?: string } | null>(null);
  
  // Load saved layout from database on mount and apply it
  // This useEffect runs once when the component mounts and user is authenticated
  // It fetches the saved layout from the database and applies it to the dashboard
  React.useEffect(() => {
    // Prevent multiple loads
    if (hasLoadedFromAPIRef.current) {
      return;
    }
    
    let isMounted = true;
    
    const loadAndApplyLayout = async () => {
      try {
        console.log("DashboardV2: Loading saved layout from database...");
        const apiLayout = await loadLayoutFromAPI();
        
        if (!isMounted) {
          console.log("DashboardV2: Component unmounted, skipping layout update");
          return; // Component unmounted, don't update state
        }
        
        if (apiLayout) {
          // Apply the saved layout from database
          console.log("DashboardV2: Saved layout found in database, applying...");
          console.log("DashboardV2: Database widget order:", apiLayout.widgets.map(w => w.widgetId).join(','));
          console.log("DashboardV2: Database widget count:", apiLayout.widgets.length);
          console.log("DashboardV2: Database cols:", JSON.stringify(apiLayout.cols));
          console.log("DashboardV2: Database hidden widgets:", apiLayout.widgets.filter(w => !w.visible).map(w => w.widgetId).join(',') || 'none');
          console.log("DashboardV2: Database isFluid:", apiLayout.isFluid);
          
          // CRITICAL: Apply the saved layout - this updates widget order, visibility, column layout, and layout mode
          setCurrentLayout(apiLayout);
          
          // Update layout mode in parent component if it's different
          if (apiLayout.isFluid !== undefined && apiLayout.isFluid !== propIsFluidLayout) {
            onLayoutModeChange(apiLayout.isFluid);
          }
          
          console.log("DashboardV2: Saved layout applied successfully - setCurrentLayout called");
          hasLoadedFromAPIRef.current = true;
        } else {
          console.log("DashboardV2: No saved layout found in database, using default layout");
          // If no saved layout, keep default but mark as loaded
          hasLoadedFromAPIRef.current = true;
        }
      } catch (error) {
        console.error("DashboardV2: Error loading saved layout from database:", error);
        console.error("DashboardV2: Error details:", error);
        hasLoadedFromAPIRef.current = true; // Mark as loaded even on error to prevent retries
      }
    };
    
    // Only load from database if user is authenticated
    if (auth?.currentUser?.id && !hasLoadedFromAPIRef.current) {
      loadAndApplyLayout();
    }
    
    return () => {
      isMounted = false;
    };
  }, [auth?.currentUser?.id, onLayoutModeChange]); // Only reload when user changes, not when layout changes
  
  // Manual save to database (only called from button click)
  const saveLayout = useCallback(async (layout: DashboardLayout) => {
    console.log("DashboardV2: Manual save triggered");
    console.log("DashboardV2: Widget order:", layout.widgets.map(w => w.widgetId).join(','));
    console.log("DashboardV2: Column layout:", JSON.stringify(layout.cols));
    console.log("DashboardV2: isFluid:", layout.isFluid);
    console.log("DashboardV2: Hidden widgets:", layout.widgets.filter(w => !w.visible).map(w => w.widgetId).join(',') || 'none');
    
    // Set saving state immediately
    setSaveStatus({ success: true }); // Show "Saving..." state
    
    try {
      console.log("DashboardV2: Executing database save...");
      console.log("DashboardV2: User ID:", auth?.currentUser?.id);
      const result = await saveLayoutToStorage(layout);
      
      if (result.success) {
        setSaveStatus({ success: true });
        console.log("DashboardV2: ✅ Layout saved successfully to database");
        console.log("DashboardV2: Saved widget order:", layout.widgets.map(w => w.widgetId).join(','));
        
        // Clear success message after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus({ success: false, error: result.error });
        console.error("DashboardV2: ❌ Failed to save layout to database:", result.error);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      setSaveStatus({ success: false, error: errorMessage });
      console.error("DashboardV2: ❌ Error saving layout to database:", error);
      console.error("DashboardV2: Error stack:", error instanceof Error ? error.stack : 'No stack');
    }
  }, [auth?.currentUser?.id]);
  
  // Derive isFluidLayout from currentLayout (database is source of truth)
  // Use prop value initially, then override with database value once loaded
  const isFluidLayoutFromLayout = currentLayout.isFluid !== false; // Default to true if not set
  const isFluidLayout = hasLoadedFromAPIRef.current ? isFluidLayoutFromLayout : propIsFluidLayout;
  
  // Handle layout mode change (fluid/boxed) - updates state only
  const handleLayoutModeChangeInternal = useCallback((isFluid: boolean) => {
    const newLayout: DashboardLayout = {
      ...currentLayout,
      isFluid: isFluid,
    };
    setCurrentLayout(newLayout);
    // Also notify parent component
    onLayoutModeChange(isFluid);
    // No auto-save - user must click "Save Layout" button
  }, [currentLayout, onLayoutModeChange]);
  
  // Debug: Log when layout changes to verify it's being applied
  React.useEffect(() => {
    console.log("DashboardV2: currentLayout changed");
    console.log("DashboardV2: Current widget order:", currentLayout.widgets.map(w => w.widgetId).join(','));
    console.log("DashboardV2: Current cols:", JSON.stringify(currentLayout.cols));
    console.log("DashboardV2: Current hidden widgets:", currentLayout.widgets.filter(w => !w.visible).map(w => w.widgetId).join(',') || 'none');
    console.log("DashboardV2: Current isFluid:", currentLayout.isFluid);
  }, [currentLayout]);

  const activeClients = filteredClients.filter((c) => c.status === "Active").length;
  const customers = filteredClients.filter((c) => c.lifecycleStage === "customer").length;
  const hasActiveFilters = 
    filters.ageRange?.[0] !== undefined ||
    filters.ageRange?.[1] !== undefined ||
    filters.city ||
    filters.stateProvince ||
    (filters.planType && filters.planType.length > 0) ||
    filters.federalAgency ||
    (filters.lifecycleStage && filters.lifecycleStage.length > 0) ||
    (filters.status && filters.status.length > 0) ||
    (isAdmin && filters.selectedAgentIds.length > 0);

  // Create widget map and titles map
  // Supports both HOC-wrapped components and original children-based wrapper
  const widgetsMap = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    
    currentLayout.widgets.forEach((widget) => {
      const baseProps = {
        clients: filteredClients,
        isLoading,
        userId: userIdForWidgets, // Pass user_id filter to widgets
      };
      
      // All widgets use HOC pattern
      const WrappedComponent = WIDGET_COMPONENTS[widget.widgetId];
      if (WrappedComponent) {
        // HOC pattern: Store component reference and props separately
        // DashboardGrid will inject drag handlers when rendering
        const preCalculatedDataKey = widget.widgetId === "retirement-readiness" ? "retirementReadiness" :
                                    widget.widgetId === "aum-by-product" ? "aumByProduct" :
                                    widget.widgetId === "contribution-rate" ? "contributionRate" :
                                    widget.widgetId === "social-security" ? "socialSecurity" :
                                    widget.widgetId === "retirement-income-sources" ? "retirementIncomeSources" :
                                    widget.widgetId === "fegli-risk" ? "fegliRisk" :
                                    widget.widgetId === "age-distribution" ? "ageDistribution" :
                                    widget.widgetId === "rollover-opportunity" ? "rolloverOpportunity" :
                                    widget.widgetId === "employer-match" ? "employerMatch" :
                                    widget.widgetId === "location-insights" ? "locationInsights" :
                                    widget.widgetId === "plan-type-spread" ? "planTypeSpread" :
                                    widget.widgetId === "longevity-stress" ? "longevityStress" : null;
        
        map.set(widget.instanceId, {
          type: 'hoc',
          component: WrappedComponent,
          widget,
          props: {
            ...baseProps,
            preCalculatedData: preCalculatedDataKey ? chartDataFromJSON?.[preCalculatedDataKey as keyof typeof chartDataFromJSON] : undefined,
          },
        } as any);
      }
    });
    
    return map;
  }, [currentLayout.widgets, filteredClients, isLoading, chartDataFromJSON]);
  
  const widgetTitlesMap = useMemo(() => {
    const map = new Map<string, string>();
    currentLayout.widgets.forEach((widget) => {
      map.set(widget.instanceId, getWidgetTitle(widget.widgetId));
    });
    return map;
  }, [currentLayout.widgets]);
  
  // Layout change handler - updates state only (no auto-save)
  // User must click "Save Layout" button to persist changes
  const handleLayoutChange = useCallback((newLayout: DashboardLayout) => {
    setCurrentLayout(newLayout);
  }, []);
  
  // Hide widget handler (toggles visibility instead of removing)
  const handleHideWidget = useCallback((instanceId: string) => {
    const widget = currentLayout.widgets.find(w => w.instanceId === instanceId);
    if (!widget) return;
    
    // Toggle visibility using toggleWidgetVisibility helper
    const newLayout = toggleWidgetVisibility(currentLayout, widget.widgetId);
    setCurrentLayout(newLayout);
    // No auto-save - user must click "Save Layout" button
  }, [currentLayout]);
  
  // Toggle widget visibility handler
  const handleToggleWidget = useCallback((widgetId: string) => {
    const newLayout = toggleWidgetVisibility(currentLayout, widgetId as any);
    setCurrentLayout(newLayout);
    // No auto-save - user must click "Save Layout" button
  }, [currentLayout]);
  
  
  // Handle grid column change (1, 2, or 3 columns)
  // CSS Grid handles layout automatically, we just need to update the column count
  const handleGridColumnsChange = useCallback((columns: number) => {
    const newLayout: DashboardLayout = {
      ...currentLayout,
      cols: {
        lg: columns,   // Large screens: use selected columns
        md: columns >= 3 ? 3 : columns,  // Medium: max 3, or selected if less
        sm: columns >= 2 ? 2 : 1,        // Small: max 2, or selected if less
        xs: columns >= 2 ? 2 : 1,        // Extra small: max 2, or selected if less
        xxs: 1,                          // Mobile: always 1
      },
    };
    
    setCurrentLayout(newLayout);
    // No auto-save - user must click "Save Layout" button
  }, [currentLayout]);
  
  // Get current grid column count (from lg breakpoint)
  // Normalize to valid dropdown value (1, 2, or 3) - default to 2 if invalid
  const currentGridColumns = (() => {
    const lgValue = currentLayout.cols.lg;
    if (lgValue === 1 || lgValue === 2 || lgValue === 3) {
      return lgValue;
    }
    // If value is invalid (e.g., 4 or undefined), default to 2
    return 2;
  })();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Stats Skeleton */}
        <div className="flex flex-row space-x-2 mb-8">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="flex-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded mb-2" />
                <div className="h-3 w-32 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Widgets Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-40 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-64 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-destructive">Error Loading Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header - Matching Clients page style */}
      <div className="pt-6 sm:pt-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Analytics Dashboard</h2>
            <p className="text-muted-foreground text-xs sm:text-sm mt-1">
              Comprehensive insights across your client portfolio
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {useSampleData && (
              <Badge variant="secondary" className="text-xs">
                Sample Data
              </Badge>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await refetchClients();
                  // Reload page to refresh all data and reset state
                  window.location.reload();
                }}
                disabled={isLoading}
                className="gap-2"
                title="Refresh dashboard data"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <LayoutSwitcher
                isFluid={isFluidLayout}
                onLayoutChange={handleLayoutModeChangeInternal}
              />
              <WidgetLibrary
                layout={currentLayout}
                onToggleWidget={handleToggleWidget}
              />
              <DashboardControls
                gridColumns={currentGridColumns}
                onGridColumnsChange={handleGridColumnsChange}
              />
              <Button
                variant={saveStatus?.success === true ? "default" : saveStatus?.success === false ? "destructive" : "outline"}
                size="sm"
                onClick={async () => {
                  await saveLayout(currentLayout);
                }}
                disabled={saveStatus?.success === true}
                className="gap-2"
                title="Save layout to database"
              >
                {saveStatus?.success === true ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : saveStatus?.success === false ? (
                  <>
                    <X className="h-4 w-4" />
                    <span className="hidden sm:inline">Save Failed</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span className="hidden sm:inline">Save Layout</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="pt-2 sm:pt-4">
        <Card className="mb-4 sm:mb-6">
          <CardHeader 
            className="!p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-accent" />
                <CardTitle className="text-sm font-semibold">Filters & Agent Selection</CardTitle>
                {hasActiveFilters && (
                  <Badge variant="secondary" className="text-xs font-medium bg-accent/10 text-accent border-accent/20">
                    {[
                      filters.ageRange?.[0] !== undefined || filters.ageRange?.[1] !== undefined,
                      !!filters.city,
                      !!filters.stateProvince,
                      (filters.planType && filters.planType.length > 0),
                      !!filters.federalAgency,
                      (filters.lifecycleStage && filters.lifecycleStage.length > 0),
                      (filters.status && filters.status.length > 0),
                      (isAdmin && filters.selectedAgentIds.length > 0),
                    ].filter(Boolean).length} active
                  </Badge>
                )}
              </div>
              {filtersExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          {filtersExpanded && (
            <CardContent className="pt-2 px-4 pb-4">
              <div className="space-y-3">
                {/* Agent Selector (Admin only) */}
                {isAdmin && (
                  <div>
                    <AgentSelector />
                  </div>
                )}

                {/* Dashboard Filters */}
                <div>
                  <DashboardFilters />
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Key Metrics - Fluid Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
            </div>
            {clients.length !== filteredClients.length && (
              <Badge variant="secondary" className="text-xs">Filtered</Badge>
            )}
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-accent mb-1">{filteredClients.length}</div>
            <p className="text-xs text-muted-foreground">
              {clients.length !== filteredClients.length 
                ? `From ${clients.length} total clients`
                : "Total number of clients"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/10">
                <UserCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-accent mb-1">{activeClients}</div>
            <p className="text-xs text-muted-foreground">
              {filteredClients.length > 0 
                ? `${Math.round((activeClients / filteredClients.length) * 100)}% of total`
                : "Active client count"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <UserCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <CardTitle className="text-sm font-medium">Customers</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-accent mb-1">{customers}</div>
            <p className="text-xs text-muted-foreground">
              {filteredClients.length > 0 
                ? `${Math.round((customers / filteredClients.length) * 100)}% of total`
                : "Customer count"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Grid */}
      <DashboardGrid
        layout={currentLayout}
        widgets={widgetsMap}
        widgetTitles={widgetTitlesMap}
        onLayoutChange={handleLayoutChange}
        onHideWidget={handleHideWidget}
      />

      {/* Bottom Section: Map (full width), Knowledge Base, and About */}
      <div className="mt-12 mb-64 space-y-6">
        {/* Google Maps - Full width, only show if CRM module is enabled */}
        {auth?.currentUser?.module_crm_plan && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-accent" />
                <span className="text-accent">Client Locations</span>
              </CardTitle>
              <CardDescription>View client distribution across the United States</CardDescription>
            </CardHeader>
              <CardContent className="p-0">
                <div className="h-[600px] w-full relative">
                  <GoogleMapsProvider apiKey={config.GOOGLE_MAPS_API_KEY}>
                    {(isLoaded) => {
                      if (!isLoaded) {
                        return (
                          <div className="h-full flex items-center justify-center">
                            <p className="text-muted-foreground text-sm">Loading Google Maps...</p>
                          </div>
                        );
                      }
                      // Wait for data to load
                      if (isLoadingLocationInsights || isLoadingAllClients) {
                        return (
                          <div className="h-full flex items-center justify-center">
                            <p className="text-muted-foreground text-sm">Loading client data...</p>
                          </div>
                        );
                      }
                      
                      if (!locationDataForMap || locationDataForMap.length === 0) {
                        return (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                              <p className="text-muted-foreground text-sm mb-2">No location data available</p>
                              <p className="text-xs text-muted-foreground">
                                {allClientsForMap?.length === 0 
                                  ? "No clients found" 
                                  : `Found ${allClientsForMap?.length || 0} clients but none have state information`}
                              </p>
                            </div>
                          </div>
                        );
                      }

                      const heatmapData = locationDataForMap.map((item: { state: string; weight: number }) => ({
                        location: new window.google.maps.LatLng(37.0902, -95.7129), // Default center (not used for polygon coloring)
                        weight: item.weight,
                        state: item.state,
                      }));

                      return (
                        <MapWrapper>
                          <GoogleHeatmap
                            key={`heatmap-${heatmapData.length}-${JSON.stringify(heatmapData.map((d: { state: string }) => d.state).sort().join(','))}`}
                            heatmapData={heatmapData}
                            onStateClick={(state) => {
                              console.log("State clicked:", state);
                            }}
                          />
                        </MapWrapper>
                      );
                    }}
                  </GoogleMapsProvider>
                </div>
              </CardContent>
          </Card>
        )}

        {/* Knowledge Base and About Company - Side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-32">
          {/* Knowledge Base */}
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-accent" />
                <span className="text-accent">Knowledge Base</span>
              </CardTitle>
              <CardDescription>Explore our collection of articles and guides</CardDescription>
            </CardHeader>
            <CardContent>
              <KnowledgeBaseSection />
            </CardContent>
          </Card>

          {/* About Company */}
          <div>
            <AboutCompany />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Map Wrapper Component
 * Constrains Google Map to container height using CSS
 */
const MapWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="h-full w-full overflow-hidden" style={{ height: '600px', position: 'relative' }}>
      <style>{`
        .map-container-wrapper > div > div {
          height: 600px !important;
          max-height: 600px !important;
        }
      `}</style>
      <div className="map-container-wrapper">
        {children}
      </div>
    </div>
  );
};

/**
 * Knowledge Base Section Component
 * Simplified version for dashboard display
 */
const KnowledgeBaseSection: React.FC = () => {
  const api = XanoAPI.getInstance();
  const [articles, setArticles] = useState<KnowledgeBaseArticle[]>([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchArticles = async () => {
      try {
        const fetchedArticles = await api.getAllKnowledgeBaseArticles();
        setArticles(fetchedArticles || []);
      } catch (err) {
        console.error("Error fetching articles:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-muted-foreground text-sm">Loading articles...</p>
      </div>
    );
  }

  // Show only first 5 articles
  const displayArticles = articles.slice(0, 5);

  if (displayArticles.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground text-sm">No articles available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {displayArticles.map((article) => (
        <div key={article.id} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
          <Link
            to={`/knowledge-base/${article.slug}`}
            className="text-sm font-semibold hover:text-accent transition-colors block mb-1"
          >
            {article.title}
          </Link>
          {article.summary && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
              {article.summary}
            </p>
          )}
        </div>
      ))}
      <div className="pt-4 border-t border-border">
        <Link
          to="/knowledge-base"
          className="text-sm font-medium text-accent hover:underline inline-flex items-center gap-1"
        >
          View all articles
          <span>→</span>
        </Link>
      </div>
    </div>
  );
};

/**
 * Main Dashboard V2 Component
 */
const DashboardV2: React.FC = () => {
  const auth = useAuth();

  // Layout mode (boxed/fluid) - will be loaded from database via DashboardV2Content
  // Default to fluid until layout loads from database
  const [isFluidLayout, setIsFluidLayout] = useState<boolean>(true);
  
  // Handle layout mode change from child component
  const handleLayoutModeChange = useCallback((isFluid: boolean) => {
    setIsFluidLayout(isFluid);
  }, []);

  // Initialize filters with empty agent selection (will show own clients for agents)
  const initialFilters = {
    selectedAgentIds: [],
  };

  return (
    <div className="flex flex-col dashboard min-h-screen">
      <Header />
      <div className={`flex-1 space-y-4 pt-6 w-full mt-10 ${
        isFluidLayout 
          ? 'p-4 sm:p-6 md:p-8' 
          : 'p-4 sm:p-6 md:p-8 container mx-auto max-w-7xl'
      }`}>
        <QueryClientProvider client={queryClient}>
          <DashboardFilterProvider initialFilters={initialFilters}>
            <DashboardV2Content isFluidLayout={isFluidLayout} onLayoutModeChange={handleLayoutModeChange} />
          </DashboardFilterProvider>
        </QueryClientProvider>
      </div>
      <Footer />
    </div>
  );
};

export default DashboardV2;

